import { useEffect, useId, useRef, useState } from 'react';

interface IUseDropdownOptions {
    onOpened?: () => void;
    closeOnClick?: boolean;
}

export const useDropdown = ({ onOpened, closeOnClick = false }: IUseDropdownOptions = {}) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const dropdownId = useId();
    const buttonId = useId();
    const [isOpen, setIsOpen] = useState(false);

    // once Firefox supports anchor positioning, we won't need to do any of this manual positioning
    const positionPopover = () => {
        if (!buttonRef.current || !popoverRef.current) {
            return;
        }

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const popoverRect = popoverRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Use exact screen coordinates (viewport coordinates) for fixed positioning
        let bottomLeftX = buttonRect.left;
        let bottomLeftY = buttonRect.top + buttonRect.height;

        // Check if popover would go off the right edge of the screen
        if (buttonRect.left + popoverRect.width > viewportWidth) {
            // Adjust x to keep popover on screen
            bottomLeftX = viewportWidth - popoverRect.width;
            // Ensure it doesn't go off the left edge
            if (bottomLeftX < 0) {
                bottomLeftX = 0;
            }
        }

        // Check if popover would go off the bottom edge of the screen
        if (buttonRect.top + buttonRect.height + popoverRect.height > viewportHeight) {
            // Check if there's more space above the button
            const spaceAbove = buttonRect.top;
            const spaceBelow = viewportHeight - (buttonRect.top + buttonRect.height);

            if (spaceAbove > spaceBelow && spaceAbove >= popoverRect.height) {
                // Flip to position above the button
                bottomLeftY = buttonRect.top - popoverRect.height;
            }
        }

        // Set CSS variables with exact viewport coordinates
        popoverRef.current.style.setProperty('--_dropdown-x', `${bottomLeftX}px`);
        popoverRef.current.style.setProperty('--_dropdown-y', `${bottomLeftY}px`);
    };

    useEffect(() => {
        positionPopover();
        const handlePopoverToggle = (e: Event) => {
            const newState = (e as any).newState === 'open';
            setIsOpen(newState);

            // Calculate position when popover opens
            if (newState) {
                positionPopover();
                if (onOpened) {
                    onOpened();
                }
            }
        };

        const popoverElement = popoverRef.current;
        if (popoverElement) {
            popoverElement.addEventListener('toggle', handlePopoverToggle);
        }

        return () => {
            if (popoverElement) {
                popoverElement.removeEventListener('toggle', handlePopoverToggle);
            }
        };
    }, [onOpened]);

    // Handle scroll events to reposition dropdown when button is in sticky container
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        window.addEventListener('scroll', positionPopover, { passive: true });
        return () => {
            window.removeEventListener('scroll', positionPopover);
        };
    }, [isOpen]);

    useEffect(() => {
        const doClose = () => {
            popoverRef.current?.hidePopover();
        };

        const popoverElement = popoverRef.current;
        if (closeOnClick && popoverElement) {
            popoverElement.addEventListener('mouseup', doClose);
        }
        return () => {
            if (closeOnClick && popoverElement) {
                popoverElement.removeEventListener('mouseup', doClose);
            }
        };
    }, [closeOnClick]);

    return {
        buttonRef,
        popoverRef,
        dropdownId,
        buttonId,
        isOpen,
        positionPopover,
    };
};
